import React, { useEffect, useState, useRef } from "react";
import {
  Globe,
  MessageSquare,
  ChevronDown,
  Check,
  Share2,
  Trash2,
} from "lucide-react";
import Modal from "../ui/Modal";
import PhoneInput from "../PhoneInput";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useExhSpaceStore } from "../../store/useExhSpaceStore";
import { useSite } from "../../store/useSite";
import { useBase } from "../../store/useBase";
import { getSiteAuthUser } from "../../services/auth";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getExhibitorFormFields } from "../../services/exhibitorSpace";
import {
  submitAdditionalProfileInfo,
  getExhibitorRequestForm,
} from "../../services/exhibitorRequest";
import ExhibitorReqFormField from "../exhibitor/exhibitor-request/ExhibitorReqFormField";

const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-3.5 h-3.5 fill-white"
    aria-hidden="true"
  >
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.294 19.497h2.039L6.486 3.24H4.298L17.607 20.65z"></path>
  </svg>
);

function ExhibitorOnboarding({ isOpen, onClose, siteUser, exhibitor }) {
  const { setSiteUser, eventData, isExhibitor, isExhibitorSpace } = useSite();
  const { eventId, siteSlug } = useBase();
  const { updateProfileSettings, submitting } = useExhSpaceStore();
  const [step, setStep] = useState(0); // 0: Personal, 1: Additional, 2: Privacy, 3: Success
  const fileInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validateFile = (file, isImage = false) => {
    if (!file) return null;
    const fileName = file.name;
    const lastDotIndex = fileName.lastIndexOf(".");
    const extension =
      lastDotIndex !== -1 ? fileName.slice(lastDotIndex).toLowerCase() : "";
    const namePart =
      lastDotIndex !== -1 ? fileName.slice(0, lastDotIndex) : fileName;

    // Type validation
    if (isImage) {
      const allowedExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
      if (!allowedExtensions.includes(extension)) {
        return "Invalid file type. Only JPG, JPEG, PNG, GIF, and WEBP are allowed.";
      }
    }

    // Name validation
    const illegalChars = /[ _.,@-]/;
    if (illegalChars.test(namePart)) {
      return "File name should not contain spaces, underscores, dots, commas, @, or hyphens. Please rename the file.";
    }
    return null;
  };

  const [formData, setFormData] = useState({
    firstName:
      siteUser?.booth_member?.first_name || siteUser?.name?.split(" ")[0] || "",
    lastName:
      siteUser?.booth_member?.last_name ||
      siteUser?.name?.split(" ").slice(1).join(" ") ||
      "",
    email: siteUser?.email || "",
    company: siteUser?.booth_member?.company_name || exhibitor?.name || "",
    designation: siteUser?.booth_member?.designation || "",
    phone:
      siteUser?.booth_member?.phone || exhibitor?.primary_contact_phone || "",
    about:
      siteUser?.booth_member?.about || exhibitor?.company_description || "",
    social: "",
    selectedPlatform: "Facebook",
    socialLinks: Array.isArray(siteUser?.booth_member?.social_links)
      ? siteUser.booth_member.social_links
      : typeof siteUser?.booth_member?.social_links === "string"
        ? JSON.parse(siteUser.booth_member.social_links)
        : [],
    directMessages: true,
    dataShare: siteUser?.data_share === 1,
    profileImage: siteUser?.booth_member?.profile_image || "",
    profileImageFile: null,
    website: exhibitor?.website || "",
    companySize: "",
    industry: "",
    additional_info: (() => {
      const info = {};
      if (siteUser?.booth_member?.answers) {
        siteUser.booth_member.answers.forEach((ans) => {
          if (ans.field_name) info[ans.field_name] = ans.answer;
        });
      }
      const oldInfo = siteUser?.booth_member?.additional_info;
      const parsedOldInfo =
        typeof oldInfo === "string" ? JSON.parse(oldInfo) : oldInfo || {};
      return { ...parsedOldInfo, ...info };
    })(),
  });

  // Sync state with siteUser if it changes (e.g. after refresh)
  useEffect(() => {
    if (siteUser?.booth_member) {
      const info = {};
      if (siteUser.booth_member.answers) {
        siteUser.booth_member.answers.forEach((ans) => {
          if (ans.field_name) info[ans.field_name] = ans.answer;
        });
      }
      const oldInfo = siteUser.booth_member.additional_info;
      const parsedOldInfo =
        typeof oldInfo === "string" ? JSON.parse(oldInfo) : oldInfo || {};

      setFormData((prev) => ({
        ...prev,
        firstName:
          siteUser.booth_member.first_name ||
          siteUser.name?.split(" ")[0] ||
          prev.firstName,
        lastName:
          siteUser.booth_member.last_name ||
          siteUser.name?.split(" ").slice(1).join(" ") ||
          prev.lastName,
        email: siteUser.email || prev.email,
        company: siteUser.booth_member.company_name || prev.company,
        designation: siteUser.booth_member.designation || prev.designation,
        phone: siteUser.booth_member.phone || prev.phone,
        about: siteUser.booth_member.about || prev.about,
        profileImage: siteUser.booth_member.profile_image || prev.profileImage,
        additional_info: { ...parsedOldInfo, ...info },
      }));
    }
  }, [siteUser]);

  const [additionalFields, setAdditionalFields] = useState([]);

  const platforms = [
    "Facebook",
    "LinkedIn",
    "Instagram",
    "Telegram",
    "Medium",
    "TikTok",
    "YouTube",
    "X",
  ];

  useEffect(() => {
    const fetchAdditionalFields = async () => {
      if (!eventId || !isOpen) return;
      try {
        const res = await getExhibitorRequestForm(eventId, 2);
        console.log(res.data);

        if (res.data.message === "success") {
          setAdditionalFields(res.data.data.fields || []);
        }
      } catch (error) {
        console.error("Error fetching additional fields:", error);
      }
    };
    fetchAdditionalFields();
  }, [eventId, isOpen]);

  const handleAddSocialLink = () => {
    const { social, selectedPlatform, socialLinks } = formData;
    if (!social) return;

    if (!social.startsWith("http://") && !social.startsWith("https://")) {
      setErrors((prev) => ({
        ...prev,
        social: "Please paste the full URL (starting with https://)",
      }));
      return;
    }

    if (socialLinks.some((l) => l.platform === selectedPlatform)) {
      setErrors((prev) => ({
        ...prev,
        social: `You have already added a ${selectedPlatform} link.`,
      }));
      return;
    }

    const patterns = {
      X: /^(https?:\/\/)?(www\.)?(x|twitter)\.com\/[a-zA-Z0-9_]+\/?$/,
      Facebook: /^(https?:\/\/)?(www\.)?facebook\.com\/[a-zA-Z0-9.]+\/?$/,
      LinkedIn:
        /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|company|school)\/[a-zA-Z0-9-]+\/?$/,
      Instagram: /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9_.]+\/?$/,
      Telegram:
        /^(https?:\/\/)?(www\.)?(t\.me|telegram\.me)\/[a-zA-Z0-9_]+\/?$/,
      Medium: /^(https?:\/\/)?(www\.)?medium\.com\/@[a-zA-Z0-9_.]+\/?$/,
      TikTok: /^(https?:\/\/)?(www\.)?tiktok\.com\/@[a-zA-Z0-9_.]+\/?$/,
      YouTube:
        /^(https?:\/\/)?(www\.)?youtube\.com\/(@[a-zA-Z0-9_-]+|channel\/[a-zA-Z0-9_-]+)\/?$/,
    };

    if (!patterns[selectedPlatform].test(social)) {
      setErrors((prev) => ({
        ...prev,
        social: `The URL does not match a valid ${selectedPlatform} format.`,
      }));
      return;
    }

    setErrors((prev) => {
      const { social, ...rest } = prev;
      return rest;
    });

    setFormData({
      ...formData,
      socialLinks: [
        ...socialLinks,
        { platform: selectedPlatform, url: social },
      ],
      social: "",
    });
  };

  const handleNext = async () => {
    if (step === 0) {
      if (!formData.firstName?.trim()) {
        setErrors({ firstName: "First Name is required" });
        toast.error("First Name is required");
        return;
      }
      setStep(1);
    } else if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      await saveProfile();
    }
  };

  const saveProfile = async () => {
    const memberId = siteUser?.booth_member?.id;
    if (!memberId) {
      toast.error("Member ID not found. Internal data error.");
      return;
    }

    let payload;
    if (formData.profileImageFile) {
      payload = new FormData();
      payload.append("first_name", formData.firstName || "");
      payload.append("last_name", formData.lastName || "");
      payload.append("email", formData.email || "");
      if (formData.phone) payload.append("phone", formData.phone);
      if (formData.designation)
        payload.append("designation", formData.designation);
      if (formData.about) payload.append("about", formData.about);
      payload.append(
        "social_links",
        JSON.stringify(formData.socialLinks || []),
      );
      payload.append("data_share", formData.dataShare ? 1 : 0);
      payload.append("profile_image", formData.profileImageFile);
      if (formData.company) payload.append("company_name", formData.company);
      if (siteUser?.id) payload.append("site_user_id", siteUser.id);
      payload.append("is_first_login", 0);
      payload.append(
        "additional_info",
        JSON.stringify(formData.additional_info || {}),
      );
    } else {
      payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        designation: formData.designation,
        about: formData.about,
        social_links: formData.socialLinks,
        data_share: formData.dataShare ? 1 : 0,
        profile_image: formData.profileImage,
        company_name: formData.company,
        site_user_id: siteUser?.id,
        is_first_login: 0,
        additional_info: formData.additional_info,
      };
    }

    const success = await updateProfileSettings(memberId, payload);
    if (success) {
      // Also submit dynamic fields as a formal form submission (Context 2)
      const userRoleId =
        siteUser?.role_id ||
        siteUser?.roleId ||
        siteUser?.booth_member?.role_id;
      if (additionalFields.length > 0 && userRoleId === 23) {
        try {
          // Construct FormData for additional info (Context 2)
          const formDataObj = new FormData();
          formDataObj.append("event_id", eventId);
          formDataObj.append("member_id", memberId);

          let imageCounter = 0;
          const answers = additionalFields.map((field) => {
            const val = formData.additional_info[field.name];
            let finalValue = val || "";

            if (
              (field.type === "image" || field.type === "file") &&
              val instanceof File
            ) {
              const fieldName = `image_${imageCounter++}`;
              formDataObj.append(fieldName, val);
              finalValue = fieldName; // Backend will match this key in req.files
            }

            return {
              field_id: field.id,
              value: finalValue,
            };
          });

          formDataObj.append("answers", JSON.stringify(answers));

          await submitAdditionalProfileInfo(formDataObj);
        } catch (error) {
          console.error("Error submitting additional info history:", error);
          // We don't block the UI here as the core profile was saved
        }
      }

      // Re-fetch user to sync store without page reload
      const userRes = await getSiteAuthUser(eventId);
      if (userRes && userRes.status === 200) {
        setSiteUser(userRes.data);
      }
      setStep(3); // Success Screen
    }
  };

  const handleDone = () => {
    setSiteUser((prev) => ({ ...prev, is_first_login: 0 }));
    navigate(`/${siteSlug}/exhibitor/space/overview`);

    onClose();
  };

  const sections = [
    { label: "Personal Information", id: 0 },
    { label: "Additional Information", id: 1 },
    { label: "Privacy Preferences", id: 2 },
  ];

  const Header = ({ currentStep }) => (
    <div className="flex flex-col w-full">
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-[20px] font-bold text-gray-900 tracking-tight">
          Complete Your Profile
        </h1>
        <span className="px-2 py-0.5 border border-orange-200 text-primary-color rounded-full text-[9px] font-black tracking-widest bg-orange-50/50 uppercase">
          Setup
        </span>
      </div>

      <div className="flex items-center gap-8 border-b border-gray-100">
        {sections.map((tab) => (
          <div key={tab.id} className="pb-2.5 relative transition-all group">
            <span
              className={`text-[14px] font-bold transition-all ${currentStep === tab.id ? "text-primary-color" : "text-gray-400"}`}
            >
              {tab.label}
            </span>
            {currentStep === tab.id && (
              <div className="absolute bottom-0 left-0 w-full h-[2.5px] bg-primary-color rounded-t-full shadow-[0_-1px_4px_rgba(133,68,13,0.15)]" />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const Footer = () => (
    <div className="flex justify-end items-center gap-3 w-full">
      {step > 0 && (
        <button onClick={() => setStep(step - 1)} className="form-cancel-btn">
          Back
        </button>
      )}
      <button
        onClick={handleNext}
        disabled={submitting}
        className={`form-primary-btn ${submitting ? "opacity-70 cursor-not-allowed" : ""}`}
      >
        {submitting ? (
          <>
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Saving...
          </>
        ) : step === 2 ? (
          "Finish"
        ) : (
          "Continue"
        )}
      </button>
    </div>
  );

  const renderInfo = () => (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      maxWidth="max-w-5xl"
      title={<Header currentStep={0} />}
      footer={<Footer />}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.2fr] gap-x-8 gap-y-6 pt-1 pb-4 min-h-[480px]">
        {/* Left Column */}
        <section className="space-y-1">
          <div className="space-y-1">
            <h2 className="text-[12px] font-medium text-gray-700">
              Profile Photo
            </h2>
            <div className="flex items-center gap-5">
              <div className="w-[72px] h-[72px] bg-[#4bb635] rounded-xl flex items-center justify-center text-white text-2xl font-bold shadow-sm overflow-hidden">
                {formData.profileImage ? (
                  <img
                    src={formData.profileImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  formData.firstName?.charAt(0) || "U"
                )}
              </div>
              <div className="space-y-1">
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/jpeg, image/png, image/gif, image/webp"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const fileError = validateFile(file, true); // true for isImage
                      if (fileError) {
                        toast.error(fileError);
                        return;
                      }
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error("File size cannot exceed 5MB");
                        return;
                      }
                      const reader = new FileReader();
                      reader.onloadend = () =>
                        setFormData({
                          ...formData,
                          profileImage: reader.result,
                          profileImageFile: file,
                        });
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                <button
                  onClick={() => fileInputRef.current.click()}
                  className="text-[#3b82f6] font-bold text-[13px] hover:underline transition-all"
                >
                  Upload Photo
                </button>
                <div className="text-gray-700 text-[11px] font-medium space-y-0.5 leading-tight">
                  <p>File size: Up to 5MB</p>
                  <p>Supported file types: JPG, JPEG, PNG, GIF, WEBP</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-2 gap-y-2">
            <div className="space-y-1">
              <label className="text-[12px] font-medium text-gray-700">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.firstName}
                onChange={(e) => {
                  setFormData({ ...formData, firstName: e.target.value });
                  if (errors.firstName) setErrors({});
                }}
                className={`w-full border ${errors.firstName ? "border-red-500 ring-1 ring-red-500" : "border-gray-400"} rounded-lg h-[40px] px-3.5 text-sm font-medium focus:ring-1 outline-none transition-all placeholder:text-gray-300`}
                type="text"
                placeholder="First name"
              />
              {errors.firstName && (
                <p className="text-red-500 text-[11px] font-bold">
                  {errors.firstName}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <label className="text-[12px] font-medium text-gray-700">
                Last Name
              </label>
              <input
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                className="w-full border border-gray-400 rounded-lg h-[40px] px-3.5 text-sm font-medium focus:ring-1 outline-none transition-all placeholder:text-gray-300"
                type="text"
                placeholder="Enter your last name"
              />
            </div>
            <div className="space-y-1 col-span-2">
              <label className="text-[12px] font-medium text-gray-700">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                disabled
                value={formData.email}
                className="w-full border border-gray-100 rounded-lg h-[40px] px-3.5 bg-gray-50/50 text-gray-700 text-sm font-medium cursor-not-allowed"
                type="email"
              />
            </div>
            <div className="space-y-1 col-span-2">
              <label className="text-[12px] font-medium text-gray-700">
                Company Name
              </label>
              <input
                value={formData.company}
                onChange={(e) =>
                  setFormData({ ...formData, company: e.target.value })
                }
                className="w-full border border-gray-400 rounded-lg h-[40px] px-3.5 text-sm font-medium focus:ring-1 outline-none transition-all placeholder:text-gray-300"
                type="text"
                placeholder="Enter your company name"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[12px] font-medium text-gray-700">
                Designation
              </label>
              <input
                value={formData.designation}
                onChange={(e) =>
                  setFormData({ ...formData, designation: e.target.value })
                }
                className="w-full border border-gray-400 rounded-lg h-[40px] px-3.5 text-sm font-medium focus:ring-1 outline-none transition-all placeholder:text-gray-300"
                type="text"
                placeholder="Enter your designation"
              />
            </div>
            <div className="space-y-1 overflow-visible">
              <label className="text-[12px] font-medium text-gray-700">
                Phone
              </label>
              <PhoneInput
                country={"in"}
                value={formData.phone}
                onChange={(val) => setFormData({ ...formData, phone: val })}
                inputClass="!w-full !h-10 !text-sm !font-medium !border-gray-400 !rounded-lg focus:!ring-1 focus:!ring-[#85440d] focus:!border-[#85440d] outline-none"
                buttonClass="!border-gray-400 !rounded-l-lg !bg-white"
                containerClass="!w-full"
                inputStyle={{
                  width: "100%",
                  height: "40px",
                  borderRadius: "8px",
                  border: "1px solid #E5E7EB",
                }}
                buttonStyle={{ borderRadius: "8px 0 0 8px" }}
                dropdownStyle={{
                  bottom: "100%",
                  top: "auto",
                  marginBottom: "5px",
                  borderRadius: "12px",
                }}
                placeholder="Enter your phone number"
              />
            </div>
          </div>
        </section>

        {/* Right Column */}
        <section className="flex flex-col gap-5">
          <div className="flex flex-col flex-grow">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-[12px] font-medium text-gray-700 tracking-tight">
                About
              </h2>
              <span className="text-[12px] text-gray-700 font-medium tracking-tight">
                {formData.about?.replace(/<[^>]*>/g, "").length || 0}/10000
              </span>
            </div>
            <div className="flex-grow border border-gray-400 rounded-xl overflow-hidden focus-within:ring-1 focus-within:ring-[#85440d] transition-all bg-white flex flex-col min-h-[300px]">
              <style>{`
                .ck.ck-editor { display: flex; flex-direction: column; flex-grow: 1; border: none !important; }
                .ck.ck-editor__main { flex-grow: 1; display: flex; flex-direction: column; }
                .ck-editor__editable { flex-grow: 1 !important; min-height: 260px !important; padding: 16px 20px !important; line-height: 1.6 !important; font-size: 14px !important; font-weight: 400 !important; color: #111827 !important; border: none !important; }
                .ck.ck-toolbar { border: none !important; border-bottom: 1px solid #f3f4f6 !important; background-color: #ffffff !important; padding: 6px 10px 2px !important; }
                .ck.ck-button { border-radius: 6px !important; margin: 0 1px !important; }
                .ck.ck-button.ck-on { background: #fef2f2 !important; color: #85440d !important; }
                .ck.ck-content .ck-placeholder::before { color: #d1d5db !important; font-style: normal !important; }
              `}</style>

              <CKEditor
                editor={ClassicEditor}
                data={formData.about}
                onChange={(e, editor) =>
                  setFormData({ ...formData, about: editor.getData() })
                }
                config={{
                  placeholder: "Write something here...",
                  toolbar: [
                    "paragraph",
                    "fontFamily",
                    "fontSize",
                    "bold",
                    "italic",
                    "underline",
                    "strikethrough",
                    "|",
                    "alignment",
                    "bulletedList",
                    "numberedList",
                    "outdent",
                    "indent",
                    "|",
                    "link",
                    "blockQuote",
                    "horizontalLine",
                    "undo",
                    "redo",
                  ],
                }}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex border border-gray-300 rounded-xl overflow-hidden bg-white focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all h-[40px]">
              <div className="relative border-r border-gray-100 min-w-[120px] h-full flex items-center">
                <select
                  value={formData.selectedPlatform}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      selectedPlatform: e.target.value,
                    })
                  }
                  className="w-full h-full pl-4 pr-10 text-[13px] font-medium text-gray-700 bg-white outline-none cursor-pointer appearance-none"
                >
                  {platforms.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              <input
                value={formData.social}
                onChange={(e) => {
                  setFormData({ ...formData, social: e.target.value });
                  if (errors.social) setErrors({});
                }}
                placeholder={`Paste full ${formData.selectedPlatform} URL (https://...)`}
                className={`flex-1 px-4 text-[13px] font-medium text-gray-900 outline-none placeholder:text-gray-300 ${errors.social ? "placeholder:text-red-200" : ""}`}
                type="text"
              />
              <button
                onClick={handleAddSocialLink}
                className="px-6 border-l border-gray-100 text-[11px] font-bold text-gray-400 hover:text-primary-color uppercase tracking-widest transition-colors flex items-center justify-center"
              >
                Add
              </button>
            </div>
            {errors.social && (
              <p className="text-red-500 text-[11px] font-bold px-1">
                {errors.social}
              </p>
            )}

            <div className="flex flex-col gap-2 max-h-[60px] overflow-y-auto pr-1 custom-scrollbar">
              {formData.socialLinks.map((link, idx) => (
                <div
                  key={idx}
                  className="flex items-center bg-gray-50/80 border border-gray-400 rounded-lg overflow-hidden h-9 w-full flex-shrink-0"
                >
                  <div className="w-9 h-full bg-black flex items-center justify-center flex-shrink-0">
                    {link.platform === "X" ? (
                      <XIcon />
                    ) : (
                      <Globe className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <span className="flex-1 px-3 text-[13px] font-medium text-gray-600 truncate">
                    {link.url}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Modal>
  );

  const renderAdditionalInfo = () => (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      maxWidth="max-w-5xl"
      title={<Header currentStep={1} />}
      footer={<Footer />}
    >
      <div className="flex flex-col items-center max-w-2xl mx-auto py-8 min-h-[480px]">
        <div className="w-14 h-14 bg-[#4BB635]/10 rounded-2xl flex items-center justify-center text-[#4BB635] mb-6">
          <Globe className="w-7 h-7" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-1.5 tracking-tight text-center">
          Additional Information
        </h3>
        <p className="text-gray-700 font-medium mb-10 text-center text-sm">
          Provide more details to help attendees find you.
        </p>

        <div className="w-full flex flex-col gap-6">
          {additionalFields.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full">
              {additionalFields.map((field) => (
                <div
                  key={field.id}
                  className={`flex flex-col gap-2 ${field.type === "textarea" ? "md:col-span-2" : ""}`}
                >
                  <ExhibitorReqFormField
                    field={field}
                    value={formData.additional_info[field.name]}
                    disabled={
                      (siteUser?.role_id ||
                        siteUser?.roleId ||
                        siteUser?.booth_member?.role_id) !== 23
                    }
                    onChange={(id, val) => {
                      setFormData((prev) => ({
                        ...prev,
                        additional_info: {
                          ...prev.additional_info,
                          [field.name]: val,
                        },
                      }));
                    }}
                    onFileChange={(id, file) => {
                      if (file) {
                        const isImage =
                          field.type?.toLowerCase() === "image" ||
                          field.field_type?.toLowerCase() === "image";
                        const fileError = validateFile(file, isImage);
                        if (fileError) {
                          toast.error(fileError);
                          return;
                        }
                      }
                      setFormData((prev) => ({
                        ...prev,
                        additional_info: {
                          ...prev.additional_info,
                          [field.name]: file,
                        },
                      }));
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );

  const renderPrivacy = () => (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      maxWidth="max-w-5xl"
      title={<Header currentStep={2} />}
      footer={<Footer />}
    >
      <div className="max-w-xl mx-auto py-10 min-h-[480px]">
        <div className="flex items-center gap-6 mb-10">
          <div className="w-14 h-14 bg-[#4BB635]/10 rounded-2xl flex items-center justify-center text-[#4BB635]">
            <Share2 className="w-7 h-7" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-0.5 tracking-tight">
              Data Sharing
            </h3>
            <p className="text-gray-700 text-sm font-medium leading-relaxed">
              Control your profile visibility and data sharing
            </p>
          </div>
        </div>

        <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="pr-12">
              <h4 className="text-[15px] font-bold text-gray-900 mb-1">
                Enable Data Sharing
              </h4>
              <p className="text-gray-700 text-[11px] font-bold uppercase tracking-wider">
                Allow sharing your profile via QR code
              </p>
            </div>
            <button
              onClick={() =>
                setFormData({ ...formData, dataShare: !formData.dataShare })
              }
              className={`w-14 h-8 rounded-full relative transition-all duration-300 flex items-center px-1 shadow-inner ${formData.dataShare ? "bg-[#4BB635]" : "bg-gray-200"}`}
            >
              <div
                className={`w-6 h-6 bg-white rounded-full transition-all shadow-md transform ${formData.dataShare ? "translate-x-6" : "translate-x-0"}`}
              />
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );

  const renderWelcome = () => (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={true}
      maxWidth="max-w-2xl"
    >
      <div className="pb-8 pt-4 flex flex-col items-center text-center px-4">
        {/* Success Icon: Calendar with Checkmark and Sparkles */}
        <h3 className="text-2xl font-bold text-gray-900 mb-8 tracking-tight">
          Welcome to{" "}
          <span className="text-primary-bg-color">
            {eventData?.event_name || "the Event"}
          </span>
        </h3>
        <div className="mb-10 relative">
          <div className="w-16 h-16 bg-white rounded-2xl border-4 border-primary-bg-color relative flex flex-col items-center justify-center shadow-lg">
            {/* Calendar Rings/Decorations */}
            <div className="absolute -top-3 left-4 w-1.5 h-4 bg-primary-bg-color rounded-full" />
            <div className="absolute -top-3 right-4 w-1.5 h-4 bg-primary-bg-color rounded-full" />
            <div className="w-full h-5 bg-primary-bg-color/10 absolute top-[-2px]" />
            {/* Checkmark in Center */}
            <div className="mt-2">
              <Check
                className="w-8 h-8 text-primary-bg-color"
                strokeWidth={3}
              />
            </div>
          </div>

          {/* Confetti/Sparkles Decoration */}
          <div className="absolute -left-6 top-0 w-2 h-2 bg-primary-bg-color/40 rounded-full animate-pulse" />
          <div className="absolute -left-4 -top-6 w-1.5 h-1.5 bg-primary-bg-color/20 rounded-full" />
          <div className="absolute -right-8 top-2 w-2 h-2 bg-primary-bg-color/40 rounded-full animate-pulse" />
          <div className="absolute -right-2 -top-8 w-1.5 h-1.5 bg-primary-bg-color/20 rounded-full" />
        </div>

        {isExhibitorSpace && (
          <>
            <p className="text-gray-600 font-medium mb-10 leading-relaxed text-base max-w-sm">
              Your profile is ready! You can now manage your exhibitor space,
              schedule meetings, and connect with attendees.
            </p>

            <button onClick={handleDone} className="form-primary-btn">
              Manage Exhibitor Space
            </button>
          </>
        )}
      </div>
    </Modal>
  );

  switch (step) {
    case 0:
      return renderInfo();
    case 1:
      return renderAdditionalInfo();
    case 2:
      return renderPrivacy();
    case 3:
      return renderWelcome();
    default:
      return null;
  }
}

export default ExhibitorOnboarding;
